import Modal from 'components/ModalPopup';
import React, { useEffect, useState } from 'react';
import { Alert, Button, Group, Stack, Text, Textarea } from '@mantine/core';
import { MaterialData } from 'types/lcms/courses';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import MaterialItem from './MaterialItem';
import { useDisclosure } from '@mantine/hooks';
import { IconArrowBackUp, IconInfoCircle, IconPencil } from '@tabler/icons-react';
import InputMaterial from './InputMaterial';

const commentSchema = z.object({
  comment: z.string().optional()
});

type CommentFormData = z.infer<typeof commentSchema>;

interface ModalConfirmApprovalProps {
  opened: boolean;
  materialData: MaterialData | null;
  type: 'approved' | 'rejected';
  onClose: () => void;
  onSubmit: (_data: { comment: string; urlFix?: string; typeFix?: string }) => void;
}

const ModalConfirmApproval: React.FC<ModalConfirmApprovalProps> = ({
  onClose,
  opened,
  materialData,
  type,
  onSubmit
}) => {
  const [openedFormUpload, { open, close }] = useDisclosure(false);
  const [material, setMaterial] = useState(materialData);
  const [isUploaded, setIsUploaded] = useState(false);
  const { control, handleSubmit, reset } = useForm<CommentFormData>({
    resolver: zodResolver(commentSchema),
    defaultValues: {
      comment: ''
    }
  });

  useEffect(() => {
    setMaterial(materialData);

    return () => {
      setMaterial(null);
      setIsUploaded(false);
    };
  }, [materialData]);

  const onFormSubmit = (data: CommentFormData) => {
    if (!materialData) return;

    onSubmit({
      comment: data.comment as string,
      urlFix: material?.url === materialData?.url ? '' : material?.url,
      typeFix: material?.type === materialData?.type ? '' : material?.type
    });
    reset();
  };

  const handleCancel = () => {
    reset();
    onClose();
  };

  if (!materialData) {
    return null;
  }

  const isSmeInternal = materialData.sme.name && materialData.sme.binusianId;
  const name = isSmeInternal
    ? `${materialData.sme.name} ${materialData.sme?.binusianId ? `(${materialData.sme.binusianId})` : ''}`
    : materialData.sme.email;

  return (
    <Modal
      close={onClose}
      opened={opened}
      title={`${type === 'approved' ? 'Approve' : 'Reject'} Material`}
      size='lg'
    >
      <form onSubmit={handleSubmit(onFormSubmit)}>
        <Stack gap={24}>
          {material && (
            <MaterialItem
              {...materialData}
              url={material?.url as string}
              type={material?.type as string}
            />
          )}

          <Stack gap={8}>
            <Text fz={'sm'} fw={600} c={'dark'}>
              Submitted by:
            </Text>
            <Stack gap={0}>
              <Text fz={'sm'} c={'gray'}>
                {name}
              </Text>
              {isSmeInternal && (
                <Text fz={'xs'} c={'dimmed'}>
                  {materialData.sme.email}
                </Text>
              )}
            </Stack>
          </Stack>
          <Stack gap={8}>
            {isUploaded && (
              <Alert
                variant='light'
                color='green'
                withCloseButton
                onClose={() => setIsUploaded(false)}
                title='Alert title'
                icon={<IconInfoCircle />}
              >
                Material has been successfully updated
              </Alert>
            )}
            <Button
              fullWidth
              variant={openedFormUpload ? 'default' : 'outline'}
              leftSection={openedFormUpload ? <IconArrowBackUp /> : <IconPencil />}
              onClick={() => {
                setIsUploaded(false);
                return openedFormUpload ? close() : open();
              }}
            >
              {openedFormUpload ? 'Cancel' : 'Revise Material'}
            </Button>
            {openedFormUpload && (
              <InputMaterial
                onSuccess={(data) => {
                  setMaterial((prevState: any) => {
                    return {
                      ...prevState,
                      title: prevState + ' revised',
                      type: data?.type,
                      url: data?.url
                    };
                  });
                  setIsUploaded(true);
                  close();
                }}
              />
            )}
          </Stack>

          <Controller
            name='comment'
            control={control}
            render={({ field }) => (
              <Textarea
                {...field}
                label='Comment'
                placeholder='Add your comment (optional)'
                minRows={3}
                maxRows={6}
              />
            )}
          />

          <Group justify='flex-end' gap={12}>
            <Button variant='outline' onClick={handleCancel}>
              Cancel
            </Button>
            <Button type='submit' color={type === 'approved' ? 'green.8' : 'destructive'}>
              {type === 'approved' ? 'Approve' : 'Reject'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

export default ModalConfirmApproval;
